/**
 * AiCreditsMeter — el saldo de créditos de IA.
 *
 * El consumo de ARIA no es un límite de plan sino un SALDO: cada respuesta gasta
 * créditos según lo que cueste pensarla. Hasta ahora ese número solo se veía en
 * Ajustes → Plan, y además como cifra GASTADA. Aquí se enseña la RESTANTE, que es
 * la que decide si mandas otra pregunta, en el sitio donde se gasta.
 *
 * Se monta en DOS superficies, porque ninguna cubre la otra:
 *
 *   - `variant="strip"` — tira a ancho completo bajo la cabecera del `AIPanel`.
 *     La barra entera es lo que hace legible "cuánto me queda" de un vistazo.
 *   - `variant="chip"` — pastilla en la `TopBar`, junto al pulso de ARIA. Hace
 *     falta porque el toggle del panel NO se monta en la superficie de ARIA
 *     (`TopBar.jsx`, `{!isAriaPage && ...}`): justo en la pantalla donde más se
 *     gasta, el panel —y con él la tira— no existe.
 *
 * Las dos comparten el popover y el criterio de tono; solo cambia el disparador.
 *
 * Vive en `components/plan/` y no en `components/ai/` aunque solo hable de ARIA:
 * su dato sale de `/api/plans/usage` y lo monta la TopBar, que es la shell. En
 * `ai/` acoplaba la barra de navegación al dominio de ARIA, que por contrato
 * «consume las superficies de los demás, nunca al revés» (.dependency-cruiser.cjs,
 * regla `nadie-depende-de-ai`).
 *
 * No renderiza NADA mientras carga ni sin datos: el panel se desmonta al cerrarlo
 * (`AIPanel.jsx`, `if (!open) return null`), así que un esqueleto parpadearía en
 * cada apertura.
 *
 * No vende: la compra de créditos vive en Ajustes → Plan, que es donde está el
 * contexto de facturación y quien tiene permiso para pagar.
 *
 * Solo LEE. Refrescar el saldo tras un turno es cosa de quien lo gasta:
 * `AriaConversationContext` invalida `qk.planUsage` en su `onDone`. Que el aviso
 * salga de allí y no de aquí es lo que mantiene la frontera de dominios: `ai`
 * puede depender de la shell, nunca al revés (`nadie-depende-de-ai`).
 */

import { useTranslation } from 'react-i18next';
import { Coins } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { cn, formatNumber, formatDate } from '@/lib/utils';
import { usePlanUsage } from '@/hooks/usePlanUsage';

// Umbrales sobre lo GASTADO, como el resto de medidores de plan: aviso al 80 %,
// alarma al 95 %. El tono va en clases semánticas (`warning`/`destructive`) y no
// en los tokens de dominio de ARIA: esos codifican el módulo activo, no gravedad.
function creditTone(used, limit) {
  if (!limit) return null;
  const ratio = used / limit;
  if (ratio >= 0.95) return 'danger';
  if (ratio >= 0.8) return 'warn';
  return null;
}

export default function AiCreditsMeter({ variant = 'strip' }) {
  const { t } = useTranslation();
  const { usage } = usePlanUsage();
  const credits = usage?.credits;
  if (!credits) return null;

  const remaining = Math.max(0, Number(credits.remaining) || 0);
  const limit = Number(credits.limit) || 0;
  const used = Math.max(0, Number(credits.used) || 0);

  // Sin bolsa no hay porcentaje que enseñar, solo el saldo. Mismo criterio que
  // `PlanLimitModal`, que suprime el "llevas X de Y" cuando el límite es 0.
  const hasBar = limit > 0;
  const pctLeft = hasBar ? Math.max(0, Math.min(100, Math.round((remaining / limit) * 100))) : 0;
  const tone = hasBar ? creditTone(used, limit) : (remaining > 0 ? null : 'danger');

  const toneText = cn(
    'text-muted-foreground',
    tone === 'warn' && 'text-warning',
    tone === 'danger' && 'text-destructive',
  );

  const common = {
    type: 'button',
    'data-testid': 'ai-credits-meter',
    'aria-label': t('ai.credits.aria_label', { count: remaining }),
  };

  // Pastilla de barra superior: alto de botón (h-9) para alinear con el resto de
  // la fila, y sin barra — a ese tamaño 3 px de progreso no se leen.
  const trigger = variant === 'chip' ? (
    <button
      {...common}
      className="h-9 shrink-0 inline-flex items-center gap-1.5 rounded-md border border-border px-2 transition-colors hover:bg-muted"
    >
      <Coins className={cn('w-4 h-4 shrink-0', tone ? toneText : 'text-muted-foreground')} />
      <span className={cn('text-xs font-medium tabular-nums', tone ? toneText : 'text-foreground')}>
        {formatNumber(remaining)}
      </span>
    </button>
  ) : (
    <button
      {...common}
      className="w-full px-4 py-2 border-b border-border text-left transition-colors hover:bg-muted/50"
    >
      <div className="flex items-center gap-1.5 text-micro leading-none">
        <Coins className={cn('w-3.5 h-3.5 shrink-0', tone ? toneText : 'text-muted-foreground')} />
        <span className={cn('font-semibold tabular-nums', tone ? toneText : 'text-foreground')}>
          {formatNumber(remaining)}
        </span>
        <span className="text-muted-foreground">{t('ai.credits.unit')}</span>
        {hasBar && (
          <span className={cn('ml-auto tabular-nums', toneText)}>
            {t('ai.credits.pct_left', { pct: pctLeft })}
          </span>
        )}
      </div>
      {hasBar && (
        <Progress
          value={pctLeft}
          className={cn(
            'mt-1.5 h-[3px]',
            tone === 'warn' && '[&>div]:bg-warning',
            tone === 'danger' && '[&>div]:bg-destructive',
          )}
        />
      )}
    </button>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>

      <PopoverContent align="end" className="w-72 text-caption">
        <p className="font-medium text-foreground mb-2">{t('ai.credits.title')}</p>

        {hasBar ? (
          <>
            <p className="text-muted-foreground">
              {t('ai.credits.of_total', {
                remaining: formatNumber(remaining),
                total: formatNumber(limit),
              })}
              {' · '}
              <span className={toneText}>{t('ai.credits.pct_left', { pct: pctLeft })}</span>
            </p>
            {/* La pastilla de la barra superior no lleva barra por tamaño: aquí es
                donde el chip recupera el "cuánto me queda" de un vistazo. */}
            <Progress
              value={pctLeft}
              className={cn(
                'mt-1.5 h-1.5',
                tone === 'warn' && '[&>div]:bg-warning',
                tone === 'danger' && '[&>div]:bg-destructive',
              )}
            />
          </>
        ) : (
          <p className={cn('font-medium', remaining > 0 ? 'text-foreground' : 'text-destructive')}>
            {remaining > 0 ? formatNumber(remaining) : t('ai.credits.empty')}
          </p>
        )}

        <p className="mt-2 text-muted-foreground">{t('ai.credits.what_is_it')}</p>

        {credits.expires_at ? (
          <p className="mt-2 text-muted-foreground">
            {t('ai.credits.renews_on', { date: formatDate(credits.expires_at) })}
          </p>
        ) : null}

        {/* Los comprados no caducan y se gastan después de los del plan: sin este
            desglose, alguien con recarga no entiende por qué su saldo no cuadra
            con lo que trae su plan. */}
        {Number(credits.purchased) > 0 && (
          <p className="mt-2 text-muted-foreground">
            {t('ai.credits.purchased_note', { credits: formatNumber(credits.purchased) })}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
